/**
 * Footer component.
 * @module components/theme/Footer/Footer
 */

import { Container, Button, Grid } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL, addAppURL } from '@plone/volto/helpers';
import { map } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faXTwitter, faWhatsapp, faYoutube, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faRss } from '@fortawesome/free-solid-svg-icons';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { useGoogleAnalytics } from '../../helpers/useGoogleAnalytics';
import { usePanelConfigAndPreferences, GDPRCookies } from 'volto-gdpr-privacy/helpers';
import imgEcityclic from './img/logo-ecityclic.svg';
import Logo from '@plone/volto/components/theme/Logo/Logo';
import { Banners, Newsletter } from './';


const socialIcons = {
  facebook: faFacebookF,
  twitter: faXTwitter,
  youtube: faYoutube,
  whatsapp: faWhatsapp,
  instagram: faInstagram,
  RSS: faRss,
  rssnews: faRss,
  rssagenda: faRss
}

const messages = defineMessages({
  labelEmail: {
    id: 'My email',
    defaultMessage: 'My email',
  },
  footer: {
    id: 'Footer',
    defaultMessage: 'Footer'
  },
  warningMessage: {
    id: 'Warning Message',
    defaultMessage: "En subscriure't, acceptes rebre comunicacions de l'Ajuntament de Palafolls. Finalitat: Gestionar l'enviament de comunicacions informatives i promocionals sobre activitats municipals. Destinataris: No se cediran dades a tercers, excepte obligació legal."
  }
});


const Footer = ({ intl }) => {
  const { siteActions = [] } = useSelector(
    (state) => ({
      siteActions: state.actions?.actions?.site_actions,
    }),
    shallowEqual,
  );
  const { socialActions = [] } = useSelector(
    (state) => ({
      socialActions: state.actions?.actions?.social_actions,
    }),
    shallowEqual,
  );

  const today = new Date();

  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const cookies = new GDPRCookies();
  const { defaultPreferences } = usePanelConfigAndPreferences(cookies);
  const gdprPreferences = useSelector(
    (state) => state.gdprPrivacyConsent.preferences ?? defaultPreferences,
  );

  useGoogleAnalytics(gdprPreferences?.prof_GANALYTICS, token);

  const site = useSelector((state) => state.site.data);
  const logoUrl = flattenToAppURL(site['plone.site_logo']) ?? null;

  return (
    <footer id="footer">

      <Banners />
      <div className="portalFooter ">

        <Container className='pb-5'>

          <Grid>
            <Grid.Row columns={12}>
              <Grid.Column mobile={12} tablet={12} computer={4} className='text-center text-lg-start'>

                {logoUrl &&
                  <figure className="footerLogo" style={{ '--maskLogo-url': `url(${logoUrl})` }}>
                    <Logo />
                  </figure>
                }
                <address className='fst-normal mb-3'>
                  Plaça Major, 11<br />
                  08389 - Palafolls, Barcelona<br />
                  <a href="tel:+34937620043" className='text-decoration-none'>93 762 00 43</a>
                </address>

                {/* SNS */}
                {socialActions?.length > 0 &&
                  <div className='footerXXSS text-center text-lg-start pt-2 mb-4 mb-md-0'>
                    <p className=' mb-0 fw-bold'>Segueix-nos a</p>
                    <ul className="footerXXSS__list my-0 list-unstyled d-flex justify-content-center justify-content-lg-start">
                      {map(socialActions, (item) => (
                        <li key={item.id}>
                          <a className="footerXXSS__link" href={item.url} title={item?.title} >
                            <FontAwesomeIcon icon={socialIcons[item.id]} className='footerXXSS__icon'  />
                            <span className="visually-hidden">{item?.title}</span>
                          </a>
                        </li>
                      ))
                      }
                    </ul>
                  </div>
                }

              </Grid.Column>
              <Grid.Column mobile={12} tablet={4} computer={4}>

                {/* NAVIGATION */}
                {siteActions?.length > 0 &&
                  <nav className='footerNav pt-2 pt-md-0 pt-lg-0 my-lg-0' aria-label={intl.formatMessage(messages.footer)} >
                    <ul className='footerNav__menu list-unstyled d-flex flex-column align-items-center align-items-md-start' >
                      {map(siteActions, (item) => (
                        <li className="footerNav__item mb-2" key={item.id}>
                          <UniversalLink
                            className="footerNav__link"
                            href={item.url ? flattenToAppURL(item.url) : addAppURL(item.id)}
                          >
                            {item?.title}
                          </UniversalLink>
                        </li>
                      ))}
                      <li className='mt-4 d-none'>
                        <UniversalLink
                          className="footerNav__link"
                          href='#'
                        >
                          <FontAwesomeIcon icon={faLock} className='me-2' />
                          <FormattedMessage id="Private area" defaultMessage="Private area" />
                        </UniversalLink>
                      </li>
                    </ul>
                  </nav>
                }

              </Grid.Column>
              <Grid.Column mobile={12} tablet={8} computer={4}>

                {/* NEWSLETTER */}
                <Newsletter/>

              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Container>

        <div className="bg-secondary-light py-3 fs-sm">
          <Container className=' d-md-flex align-items-center'>
            {/* COPYRIGHT */}
            <p className='mb-md-0'>&copy;{today.getFullYear()} Ajuntament de Palafolls</p>

            {/* CREDITS */}
            <p className='footerCopy mb-0 d-flex align-items-center ms-md-auto'>
              <FormattedMessage id="Project developed by" defaultMessage="Project developed by" />
              <a className='ms-2' href="https://ecityclic.com"><img src={imgEcityclic} width="285" height="80" alt="ecityclic by espublico." /></a>
            </p>
          </Container>
        </div>

      </div>
    </footer>
  );
};


export default injectIntl(Footer);